import { useEffect, useRef, useState } from "react";
import { FileUp, Loader2, PencilLine, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateSaisie } from "@/components/DateField";
import { Panel } from "@/components/KpiCard";
import { api, errMsg, money } from "@/lib/api";

const fr = (v) => (v === null || v === undefined ? "" : Number(v).toFixed(2).replace(".", ","));
const lire = (v) => (String(v).trim() ? Number(String(v).replace(/\s/g, "").replace(",", ".")) : null);
const SOURCES = { pdf: "PDF", image: "Photo", manuel: "Saisie" };

const Ecart = ({ v, testId }) => (v === null ? <span className="text-slate-400">—</span> : (
  <span data-testid={testId} className={Math.abs(v) <= 1 ? "text-emerald-700" : "text-red-600"}>{Math.abs(v) <= 1 ? "concorde ✓" : `écart ${money(v)}`}</span>
));

// Valeurs lues (ou saisies) à vérifier avant d'enregistrer
const Formulaire = ({ init, onFini }) => {
  const [f, setF] = useState({ ...init, solde_tps: fr(init.solde_tps), solde_tvq: fr(init.solde_tvq), interets: fr(init.interets) });
  const maj = (k, v) => setF({ ...f, [k]: v });
  const enregistrer = async () => {
    const corps = { ...f, solde_tps: lire(f.solde_tps), solde_tvq: lire(f.solde_tvq), interets: lire(f.interets) };
    if ([corps.solde_tps, corps.solde_tvq, corps.interets].some((x) => x !== null && Number.isNaN(x))) { toast.error("Montant invalide"); return; }
    if (!corps.date) { toast.error("Entrez la date de l'état"); return; }
    try { await api.post("/etats-rq", corps); toast.success("État de compte enregistré"); onFini(true); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mb-4 space-y-2 rounded-md bg-slate-50 p-3" data-testid="etat-rq-formulaire">
      {init.source !== "manuel" && <p className="text-xs text-slate-600">Valeurs lues dans « {init.fichier} » : vérifiez-les avant d'enregistrer.{init.periode ? ` Période : ${init.periode}.` : ""}</p>}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-xs text-slate-500">Date</span>
        <DateSaisie value={f.date} onChange={(x) => maj("date", x)} className="h-8 w-40" data-testid="etat-rq-date" />
        <span className="text-xs text-slate-500">Solde TPS</span>
        <Input value={f.solde_tps} onChange={(e) => maj("solde_tps", e.target.value)} inputMode="decimal" className="h-8 w-28 tabular" data-testid="etat-rq-tps" />
        <span className="text-xs text-slate-500">Solde TVQ</span>
        <Input value={f.solde_tvq} onChange={(e) => maj("solde_tvq", e.target.value)} inputMode="decimal" className="h-8 w-28 tabular" data-testid="etat-rq-tvq" />
        <span className="text-xs text-slate-500">Intérêts et pénalités</span>
        <Input value={f.interets} onChange={(e) => maj("interets", e.target.value)} inputMode="decimal" className="h-8 w-24 tabular" data-testid="etat-rq-interets" />
      </div>
      <Input value={f.notes} onChange={(e) => maj("notes", e.target.value)} placeholder="Notes (intérêts, pénalités…)" className="h-8" data-testid="etat-rq-notes" />
      <p className="text-xs text-slate-500">Soldes de taxe sans les intérêts et pénalités. Positif = montant dû à Revenu Québec ; négatif = crédit (mention « CT »).</p>
      <div className="flex gap-2">
        <Button size="sm" onClick={enregistrer} className="bg-emerald-700 hover:bg-emerald-800" data-testid="etat-rq-enregistrer"><Save className="h-3.5 w-3.5 mr-1" />Enregistrer et comparer</Button>
        <Button size="sm" variant="ghost" onClick={() => onFini(false)} data-testid="etat-rq-annuler">Annuler</Button>
      </div>
    </div>
  );
};

const Taxe = ({ e, t }) => (
  <span className="text-xs" data-testid={`etat-rq-${e.id}-${t}`}>
    <b>{t.toUpperCase()}</b> RQ {e[`solde_${t}`] === null ? "—" : money(e[`solde_${t}`])} · app {money(e.app[`solde_${t}`])} · <Ecart v={e[`ecart_${t}`]} testId={`etat-rq-${e.id}-ecart-${t}`} />
  </span>
);

const Etat = ({ e, onChange }) => {
  const supprimer = async () => {
    try { await api.delete(`/etats-rq/${e.id}`); toast.success("État supprimé"); onChange(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className={`rounded-md border px-3 py-2 ${e.concorde ? "border-emerald-200" : "border-red-200 bg-red-50/40"}`} data-testid={`etat-rq-${e.id}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-mono text-sm">{e.date}</span>
        <span className="text-xs text-slate-500">{SOURCES[e.source]}{e.fichier ? ` · ${e.fichier}` : ""}</span>
        <Taxe e={e} t="tps" /><Taxe e={e} t="tvq" />
        {e.interets ? <span className="text-xs text-amber-700" data-testid={`etat-rq-${e.id}-interets`}>Intérêts et pénalités {money(e.interets)}</span> : null}
        <Button size="icon" variant="ghost" className="ml-auto h-7 w-7" onClick={supprimer} data-testid={`etat-rq-${e.id}-supprimer`}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
      {e.notes && <p className="mt-1 text-xs text-slate-500">{e.notes}</p>}
    </div>
  );
};

// États de compte de Revenu Québec : solde TPS et TVQ comparé au solde calculé par l'application
export const EtatsRqPanel = ({ version }) => {
  const [d, setD] = useState(null);
  const [v, setV] = useState(0);
  const [form, setForm] = useState(null);
  const [lecture, setLecture] = useState(false);
  const champ = useRef(null);
  useEffect(() => { api.get("/etats-rq").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [version, v]);
  const televerser = async (file) => {
    if (!file) { return; }
    const fd = new FormData();
    fd.append("file", file);
    setLecture(true);
    try { const { data } = await api.post("/etats-rq/lire", fd); setForm(data); } catch (e) { toast.error(errMsg(e)); } finally { setLecture(false); champ.current.value = ""; }
  };
  const action = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <input ref={champ} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => televerser(e.target.files[0])} data-testid="etat-rq-fichier" />
      <Button size="sm" variant="outline" disabled={lecture} onClick={() => champ.current.click()} data-testid="etat-rq-importer">
        {lecture ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileUp className="h-3.5 w-3.5 mr-1" />}{lecture ? "Lecture…" : "PDF ou photo"}
      </Button>
      <Button size="sm" variant="outline" onClick={() => setForm({ date: "", solde_tps: null, solde_tvq: null, notes: "", source: "manuel", fichier: "" })} data-testid="etat-rq-manuel"><PencilLine className="h-3.5 w-3.5 mr-1" />Saisir</Button>
    </div>
  );
  return (
    <Panel title="État de compte Revenu Québec" testId="etats-rq-panel" action={action}>
      {!d ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
        <>
          <p className="mb-3 text-xs text-slate-500">Importez le PDF de Mon dossier, une photo ou une capture (lecture automatique), ou saisissez le solde. {d.note}</p>
          {form && <Formulaire key={form.fichier + form.date} init={form} onFini={(ok) => { setForm(null); if (ok) { setV((x) => x + 1); } }} />}
          {!d.etats.length ? <p className="text-sm text-slate-500" data-testid="etats-rq-aucun">Aucun état de compte enregistré.</p>
            : <div className="space-y-2">{d.etats.map((e) => <Etat key={e.id} e={e} onChange={() => setV((x) => x + 1)} />)}</div>}
        </>
      )}
    </Panel>
  );
};
